// Runtime portal discovery is config-first.
// Precedence for primary portal keys:
// 1. window.RUNTIME_PORTALS (array of primary portal keys)
// 2. localStorage['runtime_portals'] (JSON array of primary portal keys)
// 3. import.meta.env.VITE_PORTALS (comma-separated string of primary portal keys)
// 4. portal-config.json / backend /api/config/portal

import {
  getAvailablePortalsFromConfig,
  getCachedPortalConfig,
  loadPortalConfig,
  resolvePortalConfigEntry,
  type PortalConfigMap,
} from './portalConfig';

let cachedPrimaryPortals: string[] | null = null;
let cachedAliases: string[] | null = null;

const normalizePortal = (value: unknown): string =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-');

const sanitizePortals = (values: unknown): string[] => {
  if (!Array.isArray(values)) return [];
  return values
    .map(normalizePortal)
    .filter(Boolean)
    .filter((value) => /^(?:[a-z0-9]+(?:-[a-z0-9]+)*)$/.test(value))
    .filter((value, index, array) => array.indexOf(value) === index);
};

const extractAliasesFromConfig = (config: PortalConfigMap): string[] => {
  const aliases = Object.values(config.portals || {}).flatMap((portalConfig) =>
    Array.isArray(portalConfig.aliases) ? portalConfig.aliases : []
  );
  return sanitizePortals(aliases);
};

const extractPrimaryPortalsFromConfig = (config: PortalConfigMap): string[] => {
  return sanitizePortals(getAvailablePortalsFromConfig(config));
};

const syncRuntimeGlobals = (primaryPortals: string[], aliases: string[]): void => {
  try {
    (globalThis as any).RUNTIME_PORTALS = [...primaryPortals];
  } catch {}
  try {
    (globalThis as any).RUNTIME_PORTAL_ALIASES = [...aliases];
  } catch {}
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('runtime_portals', JSON.stringify(primaryPortals));
      localStorage.setItem('runtime_portal_aliases', JSON.stringify(aliases));
    }
  } catch {}
};

const readRuntimeOverride = (key: string): string[] => {
  try {
    const runtime = (globalThis as any)?.[key];
    return sanitizePortals(runtime);
  } catch {
    return [];
  }
};

const readStorageOverride = (key: string): string[] => {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
    if (!raw) return [];
    return sanitizePortals(JSON.parse(raw));
  } catch {
    return [];
  }
};

const readEnvOverride = (key: string): string[] => {
  try {
    const raw = (import.meta as any)?.env?.[key];
    if (!raw || typeof raw !== 'string') return [];
    return sanitizePortals(raw.split(','));
  } catch {
    return [];
  }
};

const getCachedSnapshot = (): { primaryPortals: string[]; aliases: string[] } => {
  if (cachedPrimaryPortals && cachedAliases) {
    return { primaryPortals: cachedPrimaryPortals, aliases: cachedAliases };
  }

  const config = getCachedPortalConfig();
  const primaryPortals = extractPrimaryPortalsFromConfig(config);
  const aliases = extractAliasesFromConfig(config);
  if (primaryPortals.length > 0 || aliases.length > 0) {
    cachedPrimaryPortals = primaryPortals;
    cachedAliases = aliases;
    return { primaryPortals, aliases };
  }

  return { primaryPortals: [], aliases: [] };
};

export async function primeRuntimePortals(): Promise<string[]> {
  const config = await loadPortalConfig();
  const primaryPortals = extractPrimaryPortalsFromConfig(config);
  const aliases = extractAliasesFromConfig(config);
  cachedPrimaryPortals = primaryPortals;
  cachedAliases = aliases;
  syncRuntimeGlobals(primaryPortals, aliases);
  return primaryPortals;
}

export function clearRuntimePortalsCache(): void {
  cachedPrimaryPortals = null;
  cachedAliases = null;
}

export function setRuntimePortalsSnapshot(primaryPortals: unknown, aliases: unknown = []): void {
  cachedPrimaryPortals = sanitizePortals(primaryPortals);
  cachedAliases = sanitizePortals(aliases);
  syncRuntimeGlobals(cachedPrimaryPortals, cachedAliases);
}

export function getAvailablePortals(): string[] {
  if (cachedPrimaryPortals && cachedPrimaryPortals.length > 0) return cachedPrimaryPortals;

  const runtime = readRuntimeOverride('RUNTIME_PORTALS');
  if (runtime.length > 0) {
    cachedPrimaryPortals = runtime;
    return runtime;
  }

  const storage = readStorageOverride('runtime_portals');
  if (storage.length > 0) {
    cachedPrimaryPortals = storage;
    return storage;
  }

  const envVal = readEnvOverride('VITE_PORTALS');
  if (envVal.length > 0) {
    cachedPrimaryPortals = envVal;
    return envVal;
  }

  const snapshot = getCachedSnapshot();
  if (snapshot.primaryPortals.length > 0) {
    return snapshot.primaryPortals;
  }

  return [];
}

export function resolvePortalKey(portal: unknown): string {
  const normalized = normalizePortal(portal);
  if (!normalized) return '';

  const available = getAvailablePortals();
  if (available.includes(normalized)) return normalized;

  const config = getCachedPortalConfig();
  const resolved = resolvePortalConfigEntry(normalized, config);
  return resolved?.key || '';
}

export function getPrimaryPortals(): string[] {
  const aliases = new Set(getPortalAliases());
  return getAvailablePortals().filter((portal) => !aliases.has(String(portal || '').trim().toLowerCase()));
}

export function getPortalAliases(): string[] {
  if (cachedAliases && cachedAliases.length > 0) return cachedAliases;

  const runtime = readRuntimeOverride('RUNTIME_PORTAL_ALIASES');
  if (runtime.length > 0) {
    cachedAliases = runtime;
    return runtime;
  }

  const storage = readStorageOverride('runtime_portal_aliases');
  if (storage.length > 0) {
    cachedAliases = storage;
    return storage;
  }

  const envVal = readEnvOverride('VITE_PORTAL_ALIASES');
  if (envVal.length > 0) {
    cachedAliases = envVal;
    return envVal;
  }

  const snapshot = getCachedSnapshot();
  if (snapshot.aliases.length > 0) {
    return snapshot.aliases;
  }

  return [];
}

export type PortalKeyDynamic = ReturnType<typeof getAvailablePortals>[number];
